import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    IconButton,
    Snackbar,
    Alert,
    Stack,
    TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RequestService from '../../services/RequestService';
import TableComponent from '../TableComponent';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AdminCardDialog from './AdminCardDialog';

const CompanyCardsDialog = ({ open, onClose, company }) => {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
    const [generateQuantity, setGenerateQuantity] = useState('');
    const [cardToDelete, setCardToDelete] = useState(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [isCardDialogOpen, setIsCardDialogOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);

    const fetchCards = async () => {
        if (!company) return;
        try {
            setLoading(true);
            const result = await RequestService.getCardsByCompanyAsAdmin(
                company.id
            );
            setCards(result);
        } catch (error) {
            setSnackbarMessage('Erro ao buscar cartões');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateCards = async () => {
        try {
            const result = await RequestService.bulkGenerateCards(
                company.id,
                Number(generateQuantity)
            );
            setCards((prev) => [...prev, ...result]);
            setSnackbarMessage('Cartões gerados com sucesso');
            setSnackbarOpen(true);
            setGenerateDialogOpen(false);
            setGenerateQuantity('');
        } catch (err) {
            setSnackbarMessage('Erro ao gerar cartões');
            setSnackbarOpen(true);
        }
    };

    useEffect(() => {
        if (open && company) {
            fetchCards();
        }
    }, [open, company]);

    const columns = [
        { label: 'Código', field: 'code' },
        { label: 'Status', field: 'status' },
        {
            label: '',
            field: 'actions',
            align: 'right',
            renderCell: (value, row, stopPropagation) => (
                <Stack direction="row" spacing={2}>
                    <IconButton
                        onClick={() => {
                            setSelectedCard(row.id);
                            setIsCardDialogOpen(true);
                        }}
                    >
                        <VisibilityIcon sx={{ color: '#1F83FF' }} />
                    </IconButton>

                    <IconButton
                        onClick={(event) => {
                            event.stopPropagation();
                            setCardToDelete(row);
                            setConfirmDeleteOpen(true);
                        }}
                    >
                        <DeleteIcon sx={{ color: '#1F83FF' }} />
                    </IconButton>
                </Stack>
            ),
        },
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Cartões da Empresa - {company?.name} - {cards.length} cartões
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} direction="row" mb={2}>
                    <TextField
                        type="number"
                        label="Quantidade"
                        value={generateQuantity}
                        onChange={(e) => setGenerateQuantity(e.target.value)}
                    />
                    <Button
                        variant="contained"
                        onClick={handleGenerateCards}
                        disabled={!generateQuantity}
                        sx={{
                            minHeight: '47px',
                        }}
                    >
                        Gerar Cartões
                    </Button>
                </Stack>
                <TableComponent
                    columns={columns}
                    rows={cards}
                    loading={loading}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined">
                    Fechar
                </Button>
            </DialogActions>

            <AdminCardDialog
                open={isCardDialogOpen}
                onClose={() => setIsCardDialogOpen(false)}
                cardId={selectedCard}
                company={company}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity="success"
                    variant="filled"
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            <Dialog
                open={confirmDeleteOpen}
                onClose={() => setConfirmDeleteOpen(false)}
            >
                <DialogTitle>Confirmar exclusão</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza que deseja deletar o cartão{' '}
                        <strong>{cardToDelete?.code}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmDeleteOpen(false)}
                        variant="outlined"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={async () => {
                            try {
                                await RequestService.deleteCard(
                                    cardToDelete.id
                                );
                                setCards((prev) =>
                                    prev.filter((c) => c.id !== cardToDelete.id)
                                );
                                setSnackbarMessage(
                                    'Cartão deletado com sucesso'
                                );
                            } catch (error) {
                                setSnackbarMessage('Erro ao deletar cartão');
                            } finally {
                                setConfirmDeleteOpen(false);
                                setSnackbarOpen(true);
                            }
                        }}
                        variant="contained"
                        color="error"
                    >
                        Deletar
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default CompanyCardsDialog;
